import { publicHref } from '../lib/publicUrls';
import { Link } from 'react-router-dom';
import ProgramJourney from '../components/ProgramJourney';
import MediaTruthSurface from '../components/MediaTruthSurface';
import SEO from '../components/SEO';
import { MEDIA_SLOTS } from '../lib/mediaTruth';
import { ServiceBreadcrumb, ServiceFacts } from '../components/ServiceHeader';
import './PersonalTrainingPage.css';

const coachingAreas = [
  'Strength training',
  'Injury recovery',
  'Weight loss',
  'Senior and youth fitness',
  'Mobility enhancement',
];

const PersonalTrainingPage = () => (
  <main className="dv-personal-training" data-testid="personal-training-page">
    <SEO
      title="Personal Training | Delta Valley Health Club"
      description="Personal training at Delta Valley Health Club for gym beginners, post-injury recovery, strength, mobility, and individual fitness goals."
      keywords="personal training Brentwood CA, Delta Valley personal trainer, strength training, injury recovery"
    />

    <section className="dv-personal-training__hero" aria-labelledby="personal-training-title">
      <div className="dv-personal-training__hero-copy">
        <div className="dv-service-panel">
          <ServiceBreadcrumb current="Personal Training" />
          <h1
            id="personal-training-title"
            className="dv-size-heading dv-personal-training__display"
            aria-label="Make your goals a reality."
          >
            <span>Make your goals</span>
            <em>a reality.</em>
          </h1>
          <p className="dv-size-lede dv-personal-training__hero-introduction">
            One-on-one customized fitness programs designed for your goals.
          </p>
          <div className="dv-personal-training__hero-actions dv-service-actions">
            <a className="dv-button dv-button--light" href="#pt-inquiry">Pick My Trainer</a>
            <Link className="dv-button dv-button--outline-light" to={publicHref("/our-coaches?coach=jesus")}>Meet a Trainer</Link>
          </div>
        </div>
      </div>
      <div className="dv-personal-training__hero-media-frame">
        <MediaTruthSurface
          media={MEDIA_SLOTS.programsPersonalTraining}
          className="dv-personal-training__hero-media"
          loading="eager"
        />
      </div>
      <ServiceFacts items={["One-on-one coaching", "Programs built around your goals", "Free 3D body scan with your package"]} />
    </section>

    <section className="dv-personal-training__approach" aria-labelledby="training-approach-title">
      <div className="dv-personal-training__section-heading">
        <p className="dv-eyebrow">Personalized coaching</p>
        <h2 className="dv-size-heading" id="training-approach-title">
          <span>Guidance for your</span>
          <em>fitness level and goals.</em>
        </h2>
      </div>
      <div className="dv-personal-training__approach-copy">
        <p className="dv-size-body">
          Tailored for all fitness levels and goals – from gym beginners to
          post-injury recovery and aspiring bodybuilders. Our diverse trainers
          are here to guide you on your unique journey.
        </p>
      </div>
    </section>

    <section className="dv-personal-training__coach" aria-labelledby="featured-trainer-title">
      <div className="dv-personal-training__portrait-hold" role="img" aria-label="Trainer portrait pending approval">
        <span aria-hidden="true">JA</span>
        <small>Trainer portrait pending approval</small>
      </div>
      <div className="dv-personal-training__coach-copy">
        <p className="dv-eyebrow">Featured trainer</p>
        <h2 className="dv-size-heading" id="featured-trainer-title">Jesus Argueta</h2>
        <p className="dv-personal-training__credentials">
          NSCA-Certified · Bachelor of Science in Kinesiology
        </p>
        <p className="dv-size-body dv-personal-training__coach-introduction">
          Jesus is a seasoned Personal Trainer, bringing 15 years of expertise
          to each session. With NSCA certification and a Bachelor of Science in
          Kinesiology, Jesus specializes in strength training, injury recovery,
          weight loss, senior and youth fitness, and mobility enhancement,
          empowering clients to achieve their fitness goals effectively.
        </p>
        <ol className="dv-personal-training__expertise" aria-label="Coaching areas">
          {coachingAreas.map((area, index) => (
            <li className="dv-size-body" key={area}>
              <span aria-hidden="true">{String(index + 1).padStart(2, '0')}</span>
              {area}
            </li>
          ))}
        </ol>
        <Link className="dv-text-link" to={publicHref("/our-coaches?coach=jesus")}>
          View trainer profile <span aria-hidden="true">↗</span>
        </Link>
      </div>
    </section>

    <section className="dv-personal-training__body-scan" aria-labelledby="body-scan-title">
      <div>
        <p className="dv-eyebrow">Included supporting service</p>
        <h2 className="dv-size-heading" id="body-scan-title">3D Body Scan</h2>
      </div>
      <div className="dv-personal-training__body-scan-copy">
        <p className="dv-size-body">
          Invest in you. Get a free 3D body scan included with your package.
          Start today!
        </p>
        <p className="dv-size-body">
          Use the scan to set goals and monitor changes in your body shape and
          dimensions as your body responds to your fitness program and
          nutritional guidance.
        </p>
      </div>
    </section>

    <section className="dv-personal-training__next-step" aria-labelledby="personal-training-next-step-title">
      <div className="dv-personal-training__next-step-heading">
        <p className="dv-eyebrow">Transform with us</p>
        <h2 className="dv-size-heading" id="personal-training-next-step-title">
          <span>Choose your</span>
          <em>next step.</em>
        </h2>
      </div>
      <div className="dv-personal-training__next-step-actions">
        <p className="dv-size-body">
          Ask the training team about current options, tour the Brentwood club,
          or explore membership information.
        </p>
        <div>
          <a className="dv-button dv-button--light" href="#pt-inquiry">Pick My Trainer</a>
          <Link className="dv-button dv-button--outline" to={publicHref("/book-a-tour")}>Book a Tour</Link>
          <Link className="dv-text-link" to={publicHref("/membership")}>
            Membership <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </div>
    </section>

    <ProgramJourney
      id="pt-inquiry" tone="green" testId="pt-inquiry-journey" photo="P03"
      source="personal-training-interest"
      eyebrow="Personal Training inquiry"
      title={['Start the conversation.', 'Find your trainer in about a minute.']}
      lede="Tell us how to reach you and our training team will follow up with current options and next steps."
      note={<Link to={publicHref("/programs")}>Return to All Programs</Link>}
      planTitle="Your training request" request={['Program', 'Personal Training']}
      reviewNoun="training request" startLabel="Start My Training Request" buttonText="Pick My Trainer"
      successTitle="Training Request Received"
      successMessage="Our personal training team will contact you with current options and next steps."
    />
  </main>
);

export default PersonalTrainingPage;
